import { useState } from 'react'
import { Search, X, ChevronDown, Eye, EyeOff } from 'lucide-react'
import { StatusProjetoRef } from '../tipos/projeto'
import { PessoaRef } from '../tipos/projeto'

interface KanbanFiltersProps {
  filters: {
    analistaSMNId?: number | null
    statusCodigo?: number | null
    search?: string | null
  }
  onFiltersChange: (filters: {
    analistaSMNId?: number | null
    statusCodigo?: number | null
    search?: string | null
  }) => void
  statusProjetos: StatusProjetoRef[]
  analistasSMN: PessoaRef[]
  activeFilterCount: number
  visibleColumns: Set<number>
  allColumns: Array<{ id: string; title: string; statusCodigo: number }>
  onToggleColumn: (statusCodigo: number) => void
  onSelectAllColumns: (selectAll: boolean) => void
}

export function KanbanFilters({
  filters,
  onFiltersChange,
  statusProjetos,
  analistasSMN,
  activeFilterCount,
  visibleColumns,
  allColumns,
  onToggleColumn,
  onSelectAllColumns,
}: KanbanFiltersProps) {
  const [localSearch, setLocalSearch] = useState(filters.search || '')
  const [showColumnFilter, setShowColumnFilter] = useState(false)

  const handleSearchChange = (value: string) => {
    setLocalSearch(value)
    onFiltersChange({
      ...filters,
      search: value || null,
    })
  }

  const handleAnalistaChange = (value: string) => {
    onFiltersChange({
      ...filters,
      analistaSMNId: value ? Number(value) : null,
    })
  }

  const handleStatusChange = (value: string) => {
    onFiltersChange({
      ...filters,
      statusCodigo: value ? Number(value) : null,
    })
  }

  const clearFilters = () => {
    setLocalSearch('')
    onFiltersChange({
      analistaSMNId: null,
      statusCodigo: null,
      search: null,
    })
  }

  const hasActiveFilters = activeFilterCount > 0

  return (
    <div className="space-y-3">
      {/* Primeira linha: Filtro de Colunas e Busca */}
      <div className="flex items-center gap-3">
        {/* Filtro de Colunas - Na frente do input de pesquisa */}
        <div className="relative">
          <button
            onClick={() => setShowColumnFilter(!showColumnFilter)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 whitespace-nowrap bg-white"
          >
            <Eye className="w-4 h-4" />
            Colunas
            <span className="bg-gray-200 text-gray-700 text-xs px-1.5 py-0.5 rounded-full font-medium">
              {visibleColumns.size}
            </span>
          </button>

          {/* Dropdown de Colunas */}
          {showColumnFilter && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowColumnFilter(false)}
              />
              <div className="absolute left-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-3 z-20 min-w-[220px]">
                <div className="text-xs font-semibold text-gray-700 mb-2">Exibir Colunas</div>
                <div className="space-y-1 max-h-[300px] overflow-y-auto">
                  {/* Checkbox "Selecionar todas" */}
                  <label
                    className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer border-b border-gray-200 mb-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={visibleColumns.size === allColumns.length && allColumns.length > 0}
                      onChange={(e) => {
                        e.stopPropagation()
                        onSelectAllColumns(e.target.checked)
                      }}
                      className="w-4 h-4 text-brand-500 border-gray-300 rounded focus:ring-brand-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Selecionar todas</span>
                  </label>
                  
                  {/* Lista de colunas */}
                  {allColumns.map((col) => {
                    const isVisible = visibleColumns.has(col.statusCodigo)
                    return (
                      <label
                        key={col.id}
                        className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={isVisible}
                          onChange={(e) => {
                            e.stopPropagation()
                            onToggleColumn(col.statusCodigo)
                          }}
                          className="w-4 h-4 text-brand-500 border-gray-300 rounded focus:ring-brand-500"
                        />
                        {isVisible ? (
                          <Eye className="w-4 h-4 text-gray-600" />
                        ) : (
                          <EyeOff className="w-4 h-4 text-gray-400" />
                        )}
                        <span className="text-sm text-gray-700">{col.title}</span>
                      </label>
                    )
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Busca - Ocupa espaço flexível */}
        <div className="flex-1 min-w-[250px] max-w-[400px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              value={localSearch}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Buscar projetos..."
              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white transition-all"
            />
          </div>
        </div>

        {/* Contador de Filtros e Botão Limpar */}
        <div className="flex items-center gap-2">
          {/* Contador de Filtros Ativos */}
          <div className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white">
            <span className="text-gray-600">Filtros:</span>
            <span className="bg-brand-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">
              {activeFilterCount}
            </span>
          </div>

          <button
            onClick={clearFilters}
            disabled={!hasActiveFilters}
            className={`px-3 py-2 text-sm border border-gray-300 rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap ${
              hasActiveFilters
                ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 cursor-pointer bg-white'
                : 'text-gray-400 cursor-not-allowed bg-gray-50'
            }`}
            title={hasActiveFilters ? 'Limpar todos os filtros' : 'Nenhum filtro ativo'}
          >
            <X className="w-4 h-4" />
            Limpar
          </button>
        </div>
      </div>

      {/* Segunda linha: Filtros de Seleção */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Filtro por Analista SMN */}
        <div className="relative">
          <label className="block text-xs font-medium text-gray-700 mb-1">Analista SMN</label>
          <div className="relative">
            <select
              value={filters.analistaSMNId || ''}
              onChange={(e) => handleAnalistaChange(e.target.value)}
              className="appearance-none pl-4 pr-10 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white hover:border-gray-400 transition-all cursor-pointer w-[200px]"
            >
              <option value="">Todos os Analistas</option>
              {analistasSMN.map((analista) => (
                <option key={analista.idPessoa} value={analista.idPessoa}>
                  {analista.nome}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>
        </div>

        {/* Filtro por Status */}
        <div className="relative">
          <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
          <div className="relative">
            <select
              value={filters.statusCodigo || ''}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="appearance-none pl-4 pr-10 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white hover:border-gray-400 transition-all cursor-pointer w-[180px]"
            >
              <option value="">Todos os Status</option>
              {statusProjetos.map((status) => (
                <option key={status.codigo} value={status.codigo}>
                  {status.descricao}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>
        </div>
      </div>
    </div>
  )
}
